import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { planSchedule } from '../../core/schedulePlan'
import { FetchableType, prefetchTargets, upcomingTargets } from '../../core/scheduleTargets'
import { fetcher, resolveUrl } from '../../api'
import { getLogger } from '../../logger'
import { PageLoading } from '../Loading/Loading'
import { OverlayInfoText } from './OverlayInfo'
import { EmptyContent } from './EmptyContent'
import { PlaylistPlayer } from './PlaylistPlayer'
import { ContentViewer } from './ContentViewer'
import { ResolvableEvent, resolveTimezone } from 'common/scheduleResolver'
import { now as clockNow } from 'common/clock'
import { Content } from 'common/types'

const CanvasLayoutPlayer = lazy(() => import('./CanvasLayoutPlayer').then((m) => ({ default: m.CanvasLayoutPlayer })))

const log = getLogger('schedule')

const RETRY_MS = 30 * 1000
const HOUR_MS = 60 * 60 * 1000

type TargetType = 'playlist' | 'canvasLayout' | 'content'

interface ScheduleTarget {
  type: TargetType
  id: string
  name: string
  /** Present only for single-item targets; playlists and layouts are fetched by id. */
  content?: Content
}

interface PublicScheduleEvent extends ResolvableEvent {
  name: string
  target: ScheduleTarget | null
}

interface PublicSchedule {
  id: string
  name: string
  timezone: string | null
  fallback: ScheduleTarget | null
  events: PublicScheduleEvent[]
}

interface SchedulePlayerProps {
  scheduleId: string
  /** The screen's own setting; the resolver falls back to the device and then the schedule. */
  screenTimezone?: string | null
  isPreview?: boolean
}

const TargetPlayer = ({ target }: { target: ScheduleTarget }) => {
  if (target.type === 'playlist') return <PlaylistPlayer playlistId={target.id} />
  if (target.type === 'canvasLayout') {
    return (
      <Suspense fallback={null}>
        <CanvasLayoutPlayer layoutId={target.id} />
      </Suspense>
    )
  }
  return target.content ? <ContentViewer content={target.content} /> : <EmptyContent />
}

export const SchedulePlayer = ({ scheduleId, screenTimezone, isPreview }: SchedulePlayerProps) => {
  const { data: schedule, error } = useSWR<PublicSchedule>(
    resolveUrl(`schedule/${scheduleId}/public`, isPreview ? { preview: true } : {}),
    { refreshInterval: 0, revalidateOnFocus: false }
  )

  // Bumping this re-runs the resolution; the value itself is the instant we resolve for.
  const [now, setNow] = useState(() => clockNow())

  const timezone = useMemo(
    () =>
      resolveTimezone({
        screen: screenTimezone,
        device: Intl.DateTimeFormat().resolvedOptions().timeZone,
        schedule: schedule?.timezone,
      }).timezone,
    [screenTimezone, schedule?.timezone]
  )

  // Resolved on the device rather than asked of the server, so a screen that loses its
  // connection keeps switching content on time.
  const { target, sleepMs } = useMemo(
    () => (schedule ? planSchedule(schedule, now, timezone) : { target: null, sleepMs: null }),
    [schedule, now, timezone]
  )

  // Keyed on the instant as well as the delay: a boundary further out than MAX_SLEEP_MS makes
  // every cycle ask for the same sleep, and a timer that only re-arms when the delay changes
  // would fire once and then never again.
  useEffect(() => {
    if (sleepMs === null) return
    const id = setTimeout(() => setNow(clockNow()), sleepMs)
    return () => clearTimeout(id)
  }, [sleepMs, now])

  const { cache, mutate } = useSWRConfig()
  // Must match the keys PlaylistPlayer and CanvasLayoutPlayer use, or the prefetched data is never read.
  const keyFor = useCallback(
    (ref: { type: FetchableType; id: string }) =>
      ref.type === 'playlist' ? resolveUrl(`playlist/${ref.id}`) : resolveUrl(`canvas-layout/${ref.id}/public`),
    []
  )
  const store = useCallback((key: string, data: unknown) => void mutate(key, data, { revalidate: false }), [mutate])

  // Channels arrived with all their content in one response; a schedule names its targets by id,
  // so load them all up front and an event that starts while the screen is offline still plays.
  const prefetchRound = Math.floor(now / HOUR_MS)
  useEffect(() => {
    if (!schedule) return
    const targets = upcomingTargets(schedule, clockNow(), timezone).filter(
      (t): t is ScheduleTarget & { type: FetchableType } => t.type !== 'content'
    )
    prefetchTargets(targets, { keyFor, load: (key) => fetcher(key), store }).then((loaded) =>
      log(`Prefetched ${loaded.size} schedule targets`)
    )
  }, [schedule, timezone, prefetchRound, keyFor, store])

  // What is on screen. It moves to a new target only once that target can render, so a fetch that
  // fails at an event boundary keeps the last good content up instead of an error.
  const [shown, setShown] = useState<ScheduleTarget | null | undefined>(undefined)
  const shownRef = useRef(shown)
  shownRef.current = shown
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (!schedule) return
    if (!target || target.type === 'content' || shownRef.current === undefined) {
      setShown(target)
      return
    }
    const key = keyFor(target as ScheduleTarget & { type: FetchableType })
    if (cache.get(key)?.data !== undefined) {
      setShown(target)
      return
    }
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    fetcher(key)
      .then((data) => {
        if (cancelled) return
        store(key, data)
        setShown(target)
      })
      .catch((err) => {
        if (cancelled) return
        log(`Could not load ${target.type} ${target.name}, keeping the current content`, err)
        retryTimer = setTimeout(() => setRetry((n) => n + 1), RETRY_MS)
      })
    return () => {
      cancelled = true
      clearTimeout(retryTimer)
    }
  }, [schedule, target, retry, keyFor, cache, store])

  if (error && !schedule) {
    return <OverlayInfoText text={error.message || 'Error loading schedule'} />
  }

  if (!schedule) return <PageLoading />

  const current = shown === undefined ? target : shown

  log(`Schedule ${schedule.name} in ${timezone} → ${current ? `${current.type} ${current.name}` : 'nothing scheduled'}`)

  // No event and no fallback is a valid setup — the screen is meant to be dark right now.
  if (!current) return <EmptyContent />

  return <TargetPlayer target={current} />
}
